import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'

import { useUsers } from '../../contexts/UserContext'
import { instructorsAtom } from '../../domains/user/AtomUsers'
import GroupSessionService from '../../services/GroupSessionService'
import { avatarUrl } from '../../utils/gravatar'
import useBar from '../common/Bar'

export default function GroupSessionTable() {
  const navigate = useNavigate()
  const instructors = useAtomValue(instructorsAtom)
  const { initInstructors, fetchUser } = useUsers()
  const bar = useBar()
  const [isLoading, setIsLoading] = useState(false)
  const [sessions, setSessions] = useState([])

  // Table state
  const [orderBy, setOrderBy] = useState('date')
  const [order, setOrder] = useState('desc')
  const [instructorFilter, setInstructorFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [sportFilter, setSportFilter] = useState('all')

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)
  const [participants, setParticipants] = useState({})

  // Get all sessions
  const fetchSessions = async () => {
    setIsLoading(true)
    try {
      const filters = {
        size: 999,
      }
      const response = await GroupSessionService.getGroupSessions(filters)
      setSessions(response)
    } catch (error) {
      console.error('Failed to fetch sessions:', error)
      bar.error(error.message || 'Failed to fetch sessions')
    } finally {
      setIsLoading(false)
    }
  }

  // 初始化加载数据
  React.useEffect(() => {
    const init = async () => {
      await Promise.all([fetchSessions(), initInstructors()])
    }
    init()
  }, [])

  // Handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  // Sort function
  const sortData = (data) => {
    if (data === undefined || data.length === 0) return []

    return data.sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      // Special handling for dates
      if (orderBy === 'date') {
        aValue = dayjs(a.date)
        bValue = dayjs(b.date)
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : 1
      } else {
        return bValue < aValue ? -1 : 1
      }
    })
  }

  // Filter function
  const filterData = (data) => {
    if (data === undefined) return []

    return data.filter((session) => {
      const matchesInstructor = instructorFilter === 'all' || session.instructorId === instructorFilter
      const matchesSport = sportFilter === 'all' || session.sport === sportFilter
      const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/)

      const instructor = instructors.find((inst) => inst.value === session.instructorId)
      const instructorName = instructor ? `${instructor.firstName} ${instructor.lastName}` : ''

      const matchesSearch = searchTerms.every((term) => {
        return (
          session.title?.toLowerCase().includes(term) ||
          instructorName.toLowerCase().includes(term) ||
          session.skiResort.toLowerCase().includes(term) ||
          session.status.toLowerCase().includes(term) ||
          session.sport.toLowerCase().includes(term)
        )
      })

      return matchesInstructor && matchesSport && matchesSearch
    })
  }

  // Handle delete dialog
  const handleDeleteClick = (session) => {
    setSelectedSession(session)
    setDeleteDialogOpen(true)
  }

  // Handle session deletion
  const handleDelete = async () => {
    if (!selectedSession) return

    try {
      await GroupSessionService.deleteGroupSession(selectedSession.resourceId)
      bar.success('Session deleted successfully')
      setDeleteDialogOpen(false)
      await fetchSessions() // Reload data
    } catch (error) {
      console.error('Failed to delete session:', error)
      bar.error(error.message || 'Failed to delete session')
    } finally {
      setSelectedSession(null)
    }
  }

  // Get instructor name
  const getInstructorName = (instructorId) => {
    const instructor = instructors.find((inst) => inst.value === instructorId)
    return instructor ? `${instructor.firstName} ${instructor.lastName}` : 'Unknown Instructor'
  }

  // Load participants for a session
  const loadParticipants = async (sessionId, participantIds) => {
    if (!participantIds?.length || participants[sessionId]) return

    try {
      const participantsData = await Promise.all(participantIds.map((id) => fetchUser({ userId: id })))
      setParticipants((prev) => ({
        ...prev,
        [sessionId]: participantsData.map((participant) => ({
          id: participant.resourceId,
          name: `${participant.firstName} ${participant.lastName}`,
          email: participant.email,
          avatar: participant.avatar,
        })),
      }))
    } catch (error) {
      console.error('Error loading participants:', error)
    }
  }

  // Process data with sorting and filtering
  const processedData = sortData(filterData(sessions))

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`)
  }

  const handleEditClick = (session) => {
    navigate(`/events/sessions/${session.resourceId}/edit`)
  }

  const handleViewClick = (session) => {
    navigate(`/events/sessions/${session.resourceId}`)
  }

  // Load participants for all sessions
  React.useEffect(() => {
    const loadAllParticipants = async () => {
      const sessionsWithParticipants = processedData.filter((session) => session.participantIds?.length > 0)
      for (const session of sessionsWithParticipants) {
        if (!participants[session.resourceId]) {
          await loadParticipants(session.resourceId, session.participantIds)
        }
      }
    }
    loadAllParticipants()
  }, [processedData])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          placeholder="Search by title, instructor, location, or status"
          helperText="Multiple keywords supported (space separated)"
        />
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>Instructor Filter</InputLabel>
          <Select value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)} label="Instructor Filter">
            <MenuItem value="all">All Instructors</MenuItem>
            {instructors.map((instructor) => (
              <MenuItem key={instructor.value} value={instructor.value}>
                {instructor.firstName} {instructor.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel>Sport Filter</InputLabel>
          <Select value={sportFilter} onChange={(e) => setSportFilter(e.target.value)} label="Sport Filter">
            <MenuItem value="all">All Sports</MenuItem>
            <MenuItem value="SKI">Ski</MenuItem>
            <MenuItem value="SNOWBOARD">Snowboard</MenuItem>
            <MenuItem value="MOUNTAIN_BIKE">Mountain Bike</MenuItem>
            <MenuItem value="WATER_SPORTS">Water Sports</MenuItem>
            <MenuItem value="OTHERS">Others</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'fixed' }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>
                <TableSortLabel active={orderBy === 'title'} direction={orderBy === 'title' ? order : 'asc'} onClick={() => handleRequestSort('title')}>
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>
                <TableSortLabel active={orderBy === 'instructorId'} direction={orderBy === 'instructorId' ? order : 'asc'} onClick={() => handleRequestSort('instructorId')}>
                  Instructor
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>
                <TableSortLabel active={orderBy === 'date'} direction={orderBy === 'date' ? order : 'asc'} onClick={() => handleRequestSort('date')}>
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '12%' }}>
                <TableSortLabel active={orderBy === 'sport'} direction={orderBy === 'sport' ? order : 'asc'} onClick={() => handleRequestSort('sport')}>
                  Sport
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '12%' }}>
                <TableSortLabel active={orderBy === 'skiResort'} direction={orderBy === 'skiResort' ? order : 'asc'} onClick={() => handleRequestSort('skiResort')}>
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '13%' }}>Participants</TableCell>
              <TableCell sx={{ width: '13%' }}>
                <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleRequestSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '12%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((session) => (
              <TableRow key={session.resourceId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.title}</TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => handleUserClick(session.instructorId)}
                  >
                    {getInstructorName(session.instructorId)}
                  </Box>
                </TableCell>
                <TableCell>{dayjs(session.date).format('YYYY-MM-DD')}</TableCell>
                <TableCell>
                  <Chip label={session.sport} size="small" color={session.sport === 'SKI' ? 'primary' : 'secondary'} sx={{ height: 20 }} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.skiResort}</TableCell>
                <TableCell>
                  {session.participantIds?.length > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                        {participants[session.resourceId]?.map((participant) => (
                          <Tooltip key={participant.id} title={participant.name}>
                            <Avatar src={avatarUrl(participant)} sx={{ width: 24, height: 24 }}>
                              {participant.name?.[0]}
                            </Avatar>
                          </Tooltip>
                        ))}
                      </AvatarGroup>
                      <Typography variant="body2" color="text.secondary">
                        {session.participantIds.length}/{session.maxParticipants || '∞'}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      0/{session.maxParticipants || '∞'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={session.status}
                    size="small"
                    color={
                      session.status === 'BOOKED'
                        ? 'primary'
                        : session.status === 'COMPLETED'
                          ? 'success'
                          : session.status === 'CANCELLED'
                            ? 'error'
                            : session.status === 'FULL'
                              ? 'warning'
                              : 'default'
                    }
                    sx={{ height: 20 }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewClick(session)} sx={{ p: 0.5 }}>
                        <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Session">
                      <IconButton size="small" onClick={() => handleEditClick(session)} sx={{ p: 0.5 }}>
                        <EditIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Session">
                      <IconButton size="small" onClick={() => handleDeleteClick(session)} sx={{ p: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        disablePortal={false}
        keepMounted
      >
        <DialogTitle id="delete-dialog-title">Delete Session</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">Are you sure you want to delete this session? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" autoFocus>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
